import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { Reveal, MaskedLineInView, Eyebrow } from "@/components/Reveal";
import { PHONE_TEL, PHONE_DISPLAY, WHATSAPP_URL, EMAIL, ADDRESS } from "@/data/content";

const Contatti = () => (
  <>
    <SEO
      title="Contatti — SOPAJ | Richiedi un Sopralluogo a Nibionno, Lecco e Brianza"
      description="Hai un immobile da ristrutturare? Contatta Sopaj: sopralluogo e preventivo a Nibionno, Lecco, Brianza e in altre località. Telefono 328 702 5632, WhatsApp, e-mail sopajdemir@live.it."
      path="/contatti"
    />

    <section data-testid="contatti-hero" className="max-w-7xl mx-auto px-5 md:px-8 pt-36 md:pt-48 pb-16 md:pb-20">
      <Reveal><Eyebrow number="01" label="Contatti" /></Reveal>
      <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-anthracite max-w-3xl">
        <MaskedLineInView>Hai un immobile</MaskedLineInView>
        <MaskedLineInView delay={0.1}>
          da <span className="font-serif italic font-normal text-terracotta">ristrutturare?</span>
        </MaskedLineInView>
      </h1>
      <Reveal delay={0.2}>
        <p className="mt-8 max-w-xl text-anthracite/75 leading-relaxed text-base md:text-lg">
          Raccontaci cosa vuoi realizzare. Organizziamo un sopralluogo e valutiamo
          insieme l'intervento.
        </p>
      </Reveal>
    </section>

    <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24 md:pb-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <ContactForm />
          </Reveal>
        </div>

        <div className="lg:col-span-5 space-y-4">
          <Reveal delay={0.15}>
            <a
              href={PHONE_TEL}
              data-testid="contatti-phone-card"
              className="group flex items-center gap-5 bg-panna border border-anthracite/10 p-6 hover:border-terracotta transition-colors duration-300"
            >
              <span className="w-12 h-12 shrink-0 bg-terracotta text-ivory flex items-center justify-center">
                <Phone size={20} strokeWidth={1.5} />
              </span>
              <span>
                <span className="block font-display text-xs tracking-[0.25em] uppercase text-concrete">Telefono</span>
                <span className="block mt-1 font-display text-xl text-anthracite group-hover:text-terracotta transition-colors duration-300">{PHONE_DISPLAY}</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.22}>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contatti-whatsapp-card"
              className="group flex items-center gap-5 bg-panna border border-anthracite/10 p-6 hover:border-terracotta transition-colors duration-300"
            >
              <span className="w-12 h-12 shrink-0 bg-anthracite text-ivory flex items-center justify-center">
                <MessageCircle size={20} strokeWidth={1.5} />
              </span>
              <span>
                <span className="block font-display text-xs tracking-[0.25em] uppercase text-concrete">WhatsApp</span>
                <span className="block mt-1 font-display text-xl text-anthracite group-hover:text-terracotta transition-colors duration-300">Scrivici direttamente</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.29}>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contatti-email-card"
              className="group flex items-center gap-5 bg-panna border border-anthracite/10 p-6 hover:border-terracotta transition-colors duration-300"
            >
              <span className="w-12 h-12 shrink-0 bg-sand text-anthracite flex items-center justify-center">
                <Mail size={20} strokeWidth={1.5} />
              </span>
              <span>
                <span className="block font-display text-xs tracking-[0.25em] uppercase text-concrete">E-mail</span>
                <span className="block mt-1 font-display text-lg text-anthracite group-hover:text-terracotta transition-colors duration-300 break-all">{EMAIL}</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.36}>
            <div data-testid="contatti-address-card" className="flex items-center gap-5 bg-panna border border-anthracite/10 p-6">
              <span className="w-12 h-12 shrink-0 bg-sand text-anthracite flex items-center justify-center">
                <MapPin size={20} strokeWidth={1.5} />
              </span>
              <span>
                <span className="block font-display text-xs tracking-[0.25em] uppercase text-concrete">Sede</span>
                <span className="block mt-1 text-sm text-anthracite leading-relaxed">{ADDRESS}</span>
              </span>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.2}>
        <div data-testid="contatti-map" className="mt-16 md:mt-24">
          <h2 className="font-display text-2xl md:text-3xl tracking-tight text-anthracite mb-6">
            La nostra sede
          </h2>
          <div className="border border-anthracite/10">
            <iframe
              title="Mappa — Sopaj Impresa Edile, Via Monsignor Boffa 5, Nibionno (LC)"
              src="https://maps.google.com/maps?q=Via%20Monsignor%20Boffa%205%2C%2023895%20Nibionno%20LC%2C%20Italia&t=&z=14&ie=UTF8&iwloc=&output=embed"
              className="w-full h-[320px] md:h-[420px] grayscale-[35%] contrast-[1.02]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </Reveal>
    </section>
  </>
);

export default Contatti;
